import React, { useState } from 'react';
import { authenticatedFetchJSON } from '../utils/authenticatedFetch';
import Card from '../components/ui/Card';
import { Activity, Bolt, Thermometer, ShieldAlert, Zap, Info, Microscope, Timer, Gauge } from 'lucide-react';

type AnalysisStatus = 'IDLE' | 'LOADING' | 'SUCCESS' | 'ERROR';

const MODELS = {
    arrhenius: {
        name: "Arrhenius Model",
        desc: "High Temperature Life Test (HTOL)",
        why: "Calculates acceleration factor for temperature-dependent failures (e.g., chemical aging, diffusion). Use this to determine how many hours at 125°C equals 10 years at 25°C."
    },
    'coffin-manson': {
        name: "Coffin-Manson",
        desc: "Thermal Cycling / Shock",
        why: "Models fatigue due to thermal expansion mismatch (CTE). Use this for solder joint reliability and PCB delamination risks during powering cycling."
    },
    peck: {
        name: "Hallberg-Peck",
        desc: "Temp/Humidity Bias (THB)",
        why: "Combines Temperature and Humidity stress. Essential for '85/85' testing to predict corrosion, dendritic growth, and moisture ingress."
    },
    blacks: {
        name: "Black's Equation",
        desc: "Electromigration (Current Density)",
        why: "Predicts time-to-failure for wire bonds and IC traces carrying high current. Critical for power electronics and MOSFETs."
    }
};

const ReliabilityLabPage: React.FC = () => {
    const [activeTab, setActiveTab] = useState<'ev' | 'electronics' | 'mech'>('ev');
    const [status, setStatus] = useState<AnalysisStatus>('IDLE');
    const [result, setResult] = useState<any>(null);
    const [selectedModel, setSelectedModel] = useState<string>('arrhenius');

    // EV Motor Inputs
    const [evInputs, setEvInputs] = useState({
        rpm: 14000,
        radius_mm: 80,
        temp_c: 100,
        voltage_v: 650,
        cable_m: 10,
        alt_m: 9000,
        current_a: 100,
        duration_s: 60
    });

    // ALT Inputs
    const [altInputs, setAltInputs] = useState({
        temp_use: 25,
        temp_stress: 125,
        delta_temp_use: 10,
        delta_temp_stress: 100,
        rh_use: 50,
        rh_stress: 85,
        ea: 0.7,
        j_use: 10000,
        j_stress: 200000,
        n: 2.0
    });

    // Mechanical Inputs
    const [mechInputs, setMechInputs] = useState({
        rpm: 3000,
        bore_mm: 25,
        temp_c: 60,
        stress_amp: 100,
        stress_mean: 50,
        endurance: 200,
        preload_n: 5000,
        diameter_mm: 10,
        pitch_mm: 1.5,
        friction_coeff: 0.15
    });

    const [mechType, setMechType] = useState<'bearing' | 'fatigue' | 'fastener'>('bearing');

    const runSimulation = async (type: string, params: any, endpoint: string = '/api/v1/lab/ev-motor-risk') => {
        setStatus('LOADING');
        setResult(null);
        try {
            const data = await authenticatedFetchJSON(`${endpoint}`, {
                method: 'POST',
                body: JSON.stringify({ analysis_type: type, parameters: params })
            });

            setResult(data);
            setStatus('SUCCESS');
        } catch (error) {
            console.error(error);
            setStatus('ERROR');
        }
    };

    const runAltCalc = async () => {
        setStatus('LOADING');
        setResult(null);
        try {
            const data = await authenticatedFetchJSON(`/api/v1/lab/calculate-acceleration`, {
                method: 'POST',
                body: JSON.stringify({
                    model: selectedModel,
                    parameters: altInputs
                })
            });

            setResult(data);
            setStatus('SUCCESS');
        } catch (error) {
            console.error(error);
            setStatus('ERROR');
        }
    };

    const runMechanicalCalc = async () => {
        setStatus('LOADING');
        setResult(null);
        try {
            let type = '';
            let params = {};

            if (mechType === 'bearing') {
                type = 'bearing_life_l10';
                params = { rpm: mechInputs.rpm, bore_mm: mechInputs.bore_mm, temp_c: mechInputs.temp_c };
            } else if (mechType === 'fatigue') {
                type = 'fatigue_goodman';
                params = { stress_amp_mpa: mechInputs.stress_amp, stress_mean_mpa: mechInputs.stress_mean, endurance_limit_mpa: mechInputs.endurance };
            } else if (mechType === 'fastener') {
                type = 'bolt_torque';
                params = {
                    preload_n: mechInputs.preload_n,
                    diameter_mm: mechInputs.diameter_mm,
                    pitch_mm: mechInputs.pitch_mm,
                    friction_coeff: mechInputs.friction_coeff
                };
            }

            const data = await authenticatedFetchJSON(`/api/v1/lab/mechanical-risk`, {
                method: 'POST',
                body: JSON.stringify({ analysis_type: type, parameters: params })
            });

            setResult(data);
            setStatus('SUCCESS');
        } catch (error) {
            console.error(error);
            setStatus('ERROR');
        }
    };

    return (
        <div className="space-y-6">
            <header className="pb-4 border-b-4 border-primary flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-black tracking-tight text-slate-900 dark:text-white flex items-center gap-3">
                        <Microscope className="text-blue-600" size={32} />
                        Magnus Physics Lab
                    </h1>
                    <p className="text-slate-500 mt-1 font-medium">
                        Verified Multi-Physics Simulation Kernel (Kernel v3.0)
                    </p>
                </div>
            </header>

            {/* Tabs */}
            <div className="flex gap-4 border-b border-border">
                <button
                    onClick={() => setActiveTab('ev')}
                    className={`px-4 py-2 font-bold flex items-center gap-2 border-b-2 transition-colors ${activeTab === 'ev' ? 'border-primary text-primary' : 'border-transparent text-text-secondary'}`}
                >
                    <Zap size={18} /> EV Propulsion
                </button>
                <button
                    onClick={() => setActiveTab('electronics')}
                    className={`px-4 py-2 font-bold flex items-center gap-2 border-b-2 transition-colors ${activeTab === 'electronics' ? 'border-primary text-primary' : 'border-transparent text-text-secondary'}`}
                >
                    <Activity size={18} /> Electronics ALT
                </button>
                <button
                    onClick={() => setActiveTab('mech')}
                    className={`px-4 py-2 font-bold flex items-center gap-2 border-b-2 transition-colors ${activeTab === 'mech' ? 'border-primary text-primary' : 'border-transparent text-text-secondary'}`}
                >
                    <Gauge size={18} /> Mechanical Life
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Control Panel */}
                <div className="lg:col-span-1 space-y-6 h-fit bg-slate-50 dark:bg-slate-900/50 p-4 rounded-xl border border-border">

                    {/* EV TAB */}
                    {activeTab === 'ev' && (
                        <div className="space-y-6 animate-fade-in">
                            <div className="bg-blue-100 dark:bg-blue-900/30 p-3 rounded-lg text-sm text-blue-800 dark:text-blue-200 flex gap-2">
                                <Info className="shrink-0" size={16} />
                                <p>Simulate rigorous EV constraints including Inverter-Duty spikes, High-Altitude Corona (Paschen), and Thermal Runaway.</p>
                            </div>

                            <Card title="Motor Parameters">
                                <div className="space-y-4">
                                    <div className="grid grid-cols-2 gap-2">
                                        <div><label className="text-xs font-bold text-text-secondary">RPM</label><input type="number" value={evInputs.rpm} onChange={e => setEvInputs({ ...evInputs, rpm: Number(e.target.value) })} className="w-full p-2 border rounded bg-background" /></div>
                                        <div><label className="text-xs font-bold text-text-secondary">Voltage (V)</label><input type="number" value={evInputs.voltage_v} onChange={e => setEvInputs({ ...evInputs, voltage_v: Number(e.target.value) })} className="w-full p-2 border rounded bg-background" /></div>
                                        <div><label className="text-xs font-bold text-text-secondary">Temp (°C)</label><input type="number" value={evInputs.temp_c} onChange={e => setEvInputs({ ...evInputs, temp_c: Number(e.target.value) })} className="w-full p-2 border rounded bg-background" /></div>
                                        <div><label className="text-xs font-bold text-text-secondary">Current (A)</label><input type="number" value={evInputs.current_a} onChange={e => setEvInputs({ ...evInputs, current_a: Number(e.target.value) })} className="w-full p-2 border rounded bg-background" /></div>
                                    </div>
                                    <div className="pt-2 space-y-2">
                                        <button onClick={() => runSimulation('burst', evInputs)} className="w-full py-2 bg-red-600 hover:bg-red-700 text-white rounded font-bold shadow flex justify-between px-4 items-center"><span>Rotor Burst Risk</span> <ShieldAlert size={16} /></button>
                                        <button onClick={() => runSimulation('pdiv', evInputs)} className="w-full py-2 bg-purple-600 hover:bg-purple-700 text-white rounded font-bold shadow flex justify-between px-4 items-center"><span>Corona / PDIV Risk</span> <Bolt size={16} /></button>
                                        <button onClick={() => runSimulation('thermal_transient', evInputs)} className="w-full py-2 bg-orange-600 hover:bg-orange-700 text-white rounded font-bold shadow flex justify-between px-4 items-center"><span>Thermal Transient</span> <Thermometer size={16} /></button>
                                    </div>
                                </div>
                            </Card>
                        </div>
                    )}

                    {/* ELECTRONICS ALT TAB */}
                    {activeTab === 'electronics' && (
                        <div className="space-y-6 animate-fade-in">
                            <div>
                                <label className="block text-sm font-bold text-text-secondary mb-2">Select Reliability Model</label>
                                <select
                                    value={selectedModel}
                                    onChange={(e) => setSelectedModel(e.target.value)}
                                    className="w-full p-3 bg-background border border-primary rounded-lg font-bold shadow-sm"
                                >
                                    {Object.entries(MODELS).map(([key, val]) => (
                                        <option key={key} value={key}>{val.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg border border-indigo-100 dark:border-indigo-800">
                                <h4 className="font-bold text-indigo-700 dark:text-indigo-300 flex items-center gap-2 mb-2">
                                    <Info size={16} /> Why use this?
                                </h4>
                                <p className="text-sm text-indigo-900 dark:text-indigo-100 leading-relaxed">
                                    {MODELS[selectedModel as keyof typeof MODELS]?.why}
                                </p>
                            </div>

                            <Card title="Test Parameters">
                                <div className="space-y-4">
                                    {selectedModel === 'arrhenius' && (
                                        <>
                                            <div><label className="text-xs font-bold text-text-secondary">Field Temp (°C)</label><input type="number" value={altInputs.temp_use} onChange={e => setAltInputs({ ...altInputs, temp_use: Number(e.target.value) })} className="w-full p-2 border rounded bg-background" /></div>
                                            <div><label className="text-xs font-bold text-text-secondary">Lab Stress Temp (°C)</label><input type="number" value={altInputs.temp_stress} onChange={e => setAltInputs({ ...altInputs, temp_stress: Number(e.target.value) })} className="w-full p-2 border rounded bg-background" /></div>
                                            <div><label className="text-xs font-bold text-text-secondary">Activ. Energy (eV)</label><input type="number" step="0.1" value={altInputs.ea} onChange={e => setAltInputs({ ...altInputs, ea: Number(e.target.value) })} className="w-full p-2 border rounded bg-background" /></div>
                                        </>
                                    )}
                                    {selectedModel === 'coffin-manson' && (
                                        <>
                                            <div><label className="text-xs font-bold text-text-secondary">Field Delta T</label><input type="number" value={altInputs.delta_temp_use} onChange={e => setAltInputs({ ...altInputs, delta_temp_use: Number(e.target.value) })} className="w-full p-2 border rounded bg-background" /></div>
                                            <div><label className="text-xs font-bold text-text-secondary">Lab Shock Delta T</label><input type="number" value={altInputs.delta_temp_stress} onChange={e => setAltInputs({ ...altInputs, delta_temp_stress: Number(e.target.value) })} className="w-full p-2 border rounded bg-background" /></div>
                                        </>
                                    )}
                                    {selectedModel === 'peck' && (
                                        <>
                                            <div><label className="text-xs font-bold text-text-secondary">Field Temp (°C)</label><input type="number" value={altInputs.temp_use} onChange={e => setAltInputs({ ...altInputs, temp_use: Number(e.target.value) })} className="w-full p-2 border rounded bg-background" /></div>
                                            <div><label className="text-xs font-bold text-text-secondary">Stress Temp (°C)</label><input type="number" value={altInputs.temp_stress} onChange={e => setAltInputs({ ...altInputs, temp_stress: Number(e.target.value) })} className="w-full p-2 border rounded bg-background" /></div>
                                            <div><label className="text-xs font-bold text-text-secondary">Field RH (%)</label><input type="number" value={altInputs.rh_use} onChange={e => setAltInputs({ ...altInputs, rh_use: Number(e.target.value) })} className="w-full p-2 border rounded bg-background" /></div>
                                            <div><label className="text-xs font-bold text-text-secondary">Stress RH (%)</label><input type="number" value={altInputs.rh_stress} onChange={e => setAltInputs({ ...altInputs, rh_stress: Number(e.target.value) })} className="w-full p-2 border rounded bg-background" /></div>
                                        </>
                                    )}
                                    {selectedModel === 'blacks' && (
                                        <>
                                            <div><label className="text-xs font-bold text-text-secondary">Field Current (A/cm²)</label><input type="number" value={altInputs.j_use} onChange={e => setAltInputs({ ...altInputs, j_use: Number(e.target.value) })} className="w-full p-2 border rounded bg-background" /></div>
                                            <div><label className="text-xs font-bold text-text-secondary">Stress Current (A/cm²)</label><input type="number" value={altInputs.j_stress} onChange={e => setAltInputs({ ...altInputs, j_stress: Number(e.target.value) })} className="w-full p-2 border rounded bg-background" /></div>
                                            <div><label className="text-xs font-bold text-text-secondary">Temp (°C)</label><input type="number" value={altInputs.temp_stress} onChange={e => setAltInputs({ ...altInputs, temp_stress: Number(e.target.value) })} className="w-full p-2 border rounded bg-background" /></div>
                                            <div><label className="text-xs font-bold text-text-secondary">Current Exponent (n)</label><input type="number" step="0.1" value={altInputs.n} onChange={e => setAltInputs({ ...altInputs, n: Number(e.target.value) })} className="w-full p-2 border rounded bg-background" /></div>
                                        </>
                                    )}
                                    <button onClick={runAltCalc} className="w-full py-2.5 mt-4 bg-primary text-white rounded-lg font-bold shadow hover:shadow-lg transition-all flex justify-center items-center gap-2">
                                        <Timer size={18} /> Calculate Accel. Factor
                                    </button>
                                </div>
                            </Card>
                        </div>
                    )}

                    {/* MECHANICAL TAB */}
                    {activeTab === 'mech' && (
                        <div className="space-y-6 animate-fade-in">
                            <div className="flex gap-2">
                                <button onClick={() => setMechType('bearing')} className={`flex-1 py-2 text-sm font-bold rounded ${mechType === 'bearing' ? 'bg-amber-600 text-white shadow' : 'bg-slate-200 text-slate-600'}`}>Bearing Life</button>
                                <button onClick={() => setMechType('fatigue')} className={`flex-1 py-2 text-sm font-bold rounded ${mechType === 'fatigue' ? 'bg-amber-600 text-white shadow' : 'bg-slate-200 text-slate-600'}`}>Fatigue</button>
                                <button onClick={() => setMechType('fastener')} className={`flex-1 py-2 text-sm font-bold rounded ${mechType === 'fastener' ? 'bg-amber-600 text-white shadow' : 'bg-slate-200 text-slate-600'}`}>Fastener</button>
                            </div>

                            <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg border border-amber-100 dark:border-amber-800">
                                <h4 className="font-bold text-amber-700 dark:text-amber-300 flex items-center gap-2 mb-2">
                                    <Info size={16} /> Context
                                </h4>
                                <p className="text-sm text-amber-900 dark:text-amber-100 leading-relaxed">
                                    {mechType === 'bearing' && "Predicts Grease Life (L10h) based on Speed Factor (nD) and Temperature degradation (Kawasaki/SKF model)."}
                                    {mechType === 'fatigue' && "Evaluates Structural Fatigue using the Goodman Equation. Checks if alternating stress is safe for infinite life."}
                                    {mechType === 'fastener' && "Calculates required Torque for Target Preload using Motosh/Shigley method. Accounts for thread & head friction coefficients."}
                                </p>
                            </div>

                            <Card title="Mechanical Inputs">
                                <div className="space-y-4">
                                    {mechType === 'bearing' && (
                                        <>
                                            <div><label className="text-xs font-bold text-text-secondary">Speed (RPM)</label><input type="number" value={mechInputs.rpm} onChange={e => setMechInputs({ ...mechInputs, rpm: Number(e.target.value) })} className="w-full p-2 border rounded bg-background" /></div>
                                            <div><label className="text-xs font-bold text-text-secondary">Bore Diameter (mm)</label><input type="number" value={mechInputs.bore_mm} onChange={e => setMechInputs({ ...mechInputs, bore_mm: Number(e.target.value) })} className="w-full p-2 border rounded bg-background" /></div>
                                            <div><label className="text-xs font-bold text-text-secondary">Temp (°C)</label><input type="number" value={mechInputs.temp_c} onChange={e => setMechInputs({ ...mechInputs, temp_c: Number(e.target.value) })} className="w-full p-2 border rounded bg-background" /></div>
                                        </>
                                    )}
                                    {mechType === 'fatigue' && (
                                        <>
                                            <div><label className="text-xs font-bold text-text-secondary">Stress Amplitude (MPa)</label><input type="number" value={mechInputs.stress_amp} onChange={e => setMechInputs({ ...mechInputs, stress_amp: Number(e.target.value) })} className="w-full p-2 border rounded bg-background" /></div>
                                            <div><label className="text-xs font-bold text-text-secondary">Mean Stress (MPa)</label><input type="number" value={mechInputs.stress_mean} onChange={e => setMechInputs({ ...mechInputs, stress_mean: Number(e.target.value) })} className="w-full p-2 border rounded bg-background" /></div>
                                            <div><label className="text-xs font-bold text-text-secondary">Endurance Limit (Se)</label><input type="number" value={mechInputs.endurance} onChange={e => setMechInputs({ ...mechInputs, endurance: Number(e.target.value) })} className="w-full p-2 border rounded bg-background" /></div>
                                        </>
                                    )}
                                    {mechType === 'fastener' && (
                                        <>
                                            <div><label className="text-xs font-bold text-text-secondary">Target Preload (N)</label><input type="number" value={mechInputs.preload_n} onChange={e => setMechInputs({ ...mechInputs, preload_n: Number(e.target.value) })} className="w-full p-2 border rounded bg-background" /></div>
                                            <div><label className="text-xs font-bold text-text-secondary">Nominal Diameter (mm)</label><input type="number" value={mechInputs.diameter_mm} onChange={e => setMechInputs({ ...mechInputs, diameter_mm: Number(e.target.value) })} className="w-full p-2 border rounded bg-background" /></div>
                                            <div><label className="text-xs font-bold text-text-secondary">Thread Pitch (mm)</label><input type="number" value={mechInputs.pitch_mm} step="0.1" onChange={e => setMechInputs({ ...mechInputs, pitch_mm: Number(e.target.value) })} className="w-full p-2 border rounded bg-background" /></div>
                                            <div><label className="text-xs font-bold text-text-secondary">Friction Coeff (μ)</label><input type="number" value={mechInputs.friction_coeff} step="0.01" onChange={e => setMechInputs({ ...mechInputs, friction_coeff: Number(e.target.value) })} className="w-full p-2 border rounded bg-background" /></div>
                                        </>
                                    )}
                                    <button onClick={runMechanicalCalc} className="w-full py-2.5 mt-4 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-bold shadow hover:shadow-lg transition-all flex justify-center items-center gap-2">
                                        <Gauge size={18} /> Calculate Risk
                                    </button>
                                </div>
                            </Card>
                        </div>
                    )}
                </div>

                {/* Results View */}
                <div className="lg:col-span-2">
                    <Card title="Analysis Results" className="h-full min-h-[500px] border-2 border-dashed border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xl">
                        {status === 'IDLE' && (
                            <div className="h-full flex flex-col items-center justify-center text-text-secondary opacity-40">
                                <Microscope size={80} className="mb-6 stroke-1" />
                                <h3 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-slate-400 to-slate-600">State-of-the-Art Reliability Lab</h3>
                                <p className="mt-2 text-center max-w-md">Select a physics model on the left to begin validation. All calculations are traceable to industry standards (JEDEC, AIAG).</p>
                            </div>
                        )}
                        {status === 'LOADING' && (
                            <div className="h-full flex flex-col items-center justify-center">
                                <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary"></div>
                                <p className="mt-4 font-mono text-sm text-primary animate-pulse">Running Physics Kernel v3.0...</p>
                            </div>
                        )}
                        {status === 'SUCCESS' && result && (
                            <div className="space-y-8 animate-fade-in p-2">
                                {/* Status Banner */}
                                <div className={`p-6 rounded-2xl flex items-center gap-6 shadow-sm border ${result.status?.includes('FAIL') ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`}>
                                    <div className={`p-4 rounded-full ${result.status?.includes('FAIL') ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-600'}`}>
                                        {result.status?.includes('FAIL') ? <ShieldAlert size={40} /> : <Activity size={40} />}
                                    </div>
                                    <div>
                                        <h3 className={`font-black text-3xl ${result.status?.includes('FAIL') ? 'text-red-700' : 'text-emerald-700'}`}>
                                            {result.status || (result.acceleration_factor ? 'ACCELERATED' : 'PASS')}
                                        </h3>
                                        <p className="text-slate-600 font-medium">{result.interpretation || 'Design parameters are within calculated physics limits.'}</p>
                                    </div>
                                </div>

                                {/* Data Grid */}
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {Object.entries(result).map(([key, val]) => {
                                        if (key === 'status' || key === 'temp_history' || key === 'time_history' || key === 'interpretation') return null;
                                        return (
                                            <div key={key} className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 hover:shadow-md transition-shadow">
                                                <span className="text-xs uppercase font-bold tracking-wider text-slate-400 block mb-2">{key.replace(/_/g, ' ')}</span>
                                                <span className="text-2xl font-mono font-bold text-slate-800 dark:text-slate-200">
                                                    {typeof val === 'number' && val % 1 !== 0 ? val.toFixed(2) : String(val)}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Charts / transient Data */}
                                {result.temp_history && (
                                    <div className="mt-8">
                                        <h4 className="font-bold text-text-secondary mb-2 flex items-center gap-2"><Activity size={16} /> Transient Response (Temp vs Time)</h4>
                                        <div className="p-4 bg-slate-950 rounded-xl text-emerald-400 font-mono text-xs overflow-x-auto shadow-inner border border-slate-800">
                                            {/* Simple visualization of the array for now */}
                                            <div className="flex items-end gap-1 h-32 pt-4">
                                                {result.temp_history.map((t: number, i: number) => (
                                                    <div key={i} className="bg-emerald-500/50 hover:bg-emerald-400 w-full rounded-t" style={{ height: `${(t / 200) * 100}%` }}></div>
                                                ))}
                                            </div>
                                            <div className="flex justify-between mt-2 text-slate-500">
                                                <span>0s</span>
                                                <span>{(result.time_history?.[result.time_history.length - 1] || 60).toFixed(1)}s</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        {status === 'ERROR' && (
                            <div className="p-8 bg-red-50 text-red-900 rounded-xl border border-red-200 flex flex-col items-center">
                                <ShieldAlert size={48} className="mb-4 text-red-500" />
                                <h3 className="text-xl font-bold">Calculation Error</h3>
                                <p>Failed to connect to Magnus Kernel. Ensure the backend (port 8000) is running.</p>
                            </div>
                        )}
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default ReliabilityLabPage;
